import groupBy from 'lodash.groupby'
import orderBy from 'lodash.orderby'
import moment from 'moment'
import { Dictionary } from 'lodash'
import { todayYesterday } from '../../../utils/time'
import { uniq } from '../../../utils/sorting'

export type IndexByNumber<T> = { [key: number]: T }
export type IndexByString<T> = { [key: string]: T }

type GroupedProjectsType<T = Insite.Project> = { [key: number]: T }

/**
 * @param {array} rules array of rule objects
 * @returns {array} array of unique issue ID strings
 */
export function getUniqIssues (rules: Insite.Insights.ComposedOccurrence[]): string[] {
  const issues = rules.map(rule => rule.issueKey)
  return uniq(issues) as string[]
}

/**
 * @param {array} rules array of rule objects
 * @returns {array} array of every issue ID string, which could be duplicated
 */
export function getEveryDefinedIssue (rules: Insite.Insights.ComposedOccurrence[]) {
  const issueKeys = rules.reduce((keys: string[], rule) => {
    if (rule.issueKey !== undefined) {
      keys.push(rule.issueKey)
    }

    return keys
  }, [])

  return issueKeys
}

/**
 * Strips non-numerical characters from duration string
 * @param {string} dur duration string in the form of '1.08333333 h'
 * @returns {string} parsed string in the form of '1.08333333'
 */
function parseDuration (dur: string): string {

  const re = /\d+\.?(\d+)?/
  const parsed = dur.match(re)
  if (parsed !== null) return parsed[0]
  return '0'
}

/**
 *
 * @param {array} rules array of string float values
 * @returns {number} sum of all rule durations, to 2 decimal places
 */
function sumDuration (rules: Insite.Insights.ComposedOccurrence[] = []) {
  const sum = rules.reduce((total, rule) => total + rule.duration, 0)
  return sum
}

/**
 * @param {array} rules Array of occurrence objects
 * @returns {number} sum of all times a rule has been triggered within the 24 hour period
 */
export function sumRawHits (rules: Insite.Insights.ComposedOccurrence[] = []) {
  return rules.reduce((hits, rule) => hits + rule.times.length, 0)
}

export function sumHits (rules: Insite.Insights.ComposedInsight[] = []) {
  return rules.reduce((hits, rule) => hits + rule.activeHits, 0)
}

export function getUniqEquip (composedRules: Insite.Insights.ComposedRule[]) {
  const targetIds = composedRules.reduce<Insite.Insights.Rule[]>((flat, rule) => {
    flat.push(...rule.targets)
    return flat
  }, [])
    .map(target => target.targetId)
  return targetIds
}

function getGroupEquipCount (composedRules: Insite.Insights.ComposedInsight[]) {
  return composedRules.reduce((total, rule) => total + rule.targetCount, 0)
}

function getGroupUnassociatedEquipCount (composedRules: Insite.Insights.ComposedInsight[]) {
  return composedRules.reduce((total, rule) => total + (rule.targetCount - rule.actionsCount), 0)
}

export function getTicketCount (composedRules: Insite.Insights.ComposedRule[]) {
  return composedRules.map(rule => rule.actionsCount).reduce((sum, count) => sum + count, 0)
}

/**
 * @param {array} groupedInsights array of grouped insights
 * @param {boolean} uniqActionCount whether we want to return unique actions counts or not
 * @returns {array} array of componsed insights
 */
export function composeInsights (groupedInsights: IndexByNumber<Insite.Insights.ComposedOccurrence[]>, uniqActionCount?: boolean) {
  return Object.entries(groupedInsights).map<Insite.Insights.ComposedInsight>(([id, insight]) => {
    const targets = insight
    const activeTargets = insight.filter(({ isSnoozed }) => !isSnoozed)
    const issues = uniqActionCount ? getUniqIssues(targets) : getEveryDefinedIssue(targets)
    const activeIssues = uniqActionCount ? getUniqIssues(activeTargets) : getEveryDefinedIssue(activeTargets)
    const instance = insight.sort((a, b) => new Date(b.occurredOn).getTime() - new Date(a.occurredOn).getTime())[0]
    const { ruleName, severity, ruleType } = instance
    const latest = instance.occurredOn
    const latestUnix = new Date(instance.occurredOn).setHours(0, 0, 0, 0) / 1000
    const hits = sumRawHits(targets)
    const activeHits = sumRawHits(activeTargets)
    const service = instance.ruleType || ''
    const rawDuration = sumDuration(targets)
    const activeRawDuration = sumDuration(activeTargets)
    const duration = Math.round(rawDuration * 100) / 100
    const activeDuration = Math.round(activeRawDuration * 100) / 100
    const averageDur = Math.round((rawDuration / hits) * 100) / 100
    const activeAverageDur = Math.round((activeRawDuration / hits) * 100) / 100

    const composed = {
      id,
      site: instance.site,
      project: instance.project,
      targets,
      activeTargets,
      targetCount: targets.length,
      activeTargetCount: activeTargets.length,
      isAllSnoozed: activeTargets.length === 0,
      ruleName,
      ruleType,
      ruleId: instance.ruleId,
      latest,
      latestUnix,
      latestLabel: instance.latestLabel,
      duration,
      activeDuration,
      averageDur,
      activeAverageDur,
      hits,
      activeHits,
      service,
      severity,
      issues,
      actionsCount: issues.length,
      activeActionsCount: activeIssues.length,
      priority: instance.priority
    }

    return {
      ...composed,
      valueString: `${ruleName || ''} ${instance.ruleType || ''} ${instance.latestLabel || ''} ${severity || ''}`
    }
  })
}

const todayUnix = new Date().setHours(23, 59, 59, 0) / 1000
export function composeOccurrences (
  occurrences: Insite.API.Occurrence[],
  sites: IndexByString<Insite.Site>,
  projects: IndexByNumber<Insite.Project>
  ) {
  // @ts-ignore
  const result = occurrences.map<Insite.Insights.ComposedOccurrence>((o) => {
    const {
      siteId, reportUrl, skysparkDomain, severity, targetName, targetId, occurredOn, issueKey, description, ruleType, ruleName, snoozeEnd, ...rest
    } = o

    const times = o.times.split(',')
    const m = moment(occurredOn)
    const latestLabel = todayYesterday(m)
    const duration = Number(parseDuration(o.duration))
    const averageDur = Math.round((duration / times.length) * 100) / 100
    // @ts-ignore
    const isSnoozed = snoozeEnd !== null ? parseInt(snoozeEnd, 10) > todayUnix : false
    const site = sites[siteId]
    const project = projects[site.projectId].project_name

    return {
      ...rest,
      site,
      project,
      siteId,
      snoozeEnd,
      isSnoozed,
      severity,
      ruleType,
      ruleName,
      targetName,
      targetId,
      skysparkId: '',
      reportURL: `${skysparkDomain}${reportUrl}`,
      duration,
      description: typeof description === 'string' ? description : '',
      latestLabel,
      occurredOn,
      times,
      averageDur,
      issueKey,
      targetValueString: `${targetName || ''} ${site.label} ${site.state} ${project} ${severity || ''} ${targetId} ${issueKey || ''} ${description || ''} ${ruleType || ''} ${ruleName}`
    }
  })

  return result
}

export function groupByRule (insights: Insite.Insights.ComposedOccurrence[] = []) {
  if (insights.length < 1) return []
  const ruleGroups = groupBy(insights, o => o.ruleId)
  return Object.values(ruleGroups).map((siteInsights) => {
    const groupedInsights = groupBy(siteInsights, o => o.siteId)
    return composeInsights(groupedInsights)
  })
}

export function groupBySite (insights: Insite.Insights.ComposedOccurrence[] = []) {
  if (insights.length < 1) return []

  const ruleGroups = groupBy(insights, o => o.siteId)
  return Object.values(ruleGroups).map((siteInsights) => {
    const groupedInsights = groupBy(siteInsights, o => o.ruleId)
    return composeInsights(groupedInsights)
  })
}

type Composed = {
  site: Insite.Site;
  project: string;
  ruleGroups: Dictionary<Insite.Insights.ComposedOccurrence[]>;
  insights: Insite.Insights.ComposedInsight[];
}

export function createSiteGroups (
  insights: Insite.Insights.ComposedOccurrence[] = [],
  sites: IndexByString<Insite.Site>,
  projects: GroupedProjectsType
): Dictionary<Composed> {
  if (insights.length < 1) return {} as Dictionary<Composed>

  const siteGroups = groupBy(insights, o => o.siteId)

  const result = Object.entries(siteGroups)
    .reduce<Dictionary<Composed>>((combined, [key, siteInsights]) => {
      const instance = siteInsights[0]
      const site = sites[instance.site.syncoId]
      const project = projects[site.projectId].project_name
      const ruleGroups = groupBy(siteInsights, o => o.ruleId)
      const composed = {
        site,
        project,
        ruleGroups,
        insights: composeInsights(ruleGroups)
      }

      combined[key] = composed

      return combined
    }, {})

  return result
}

interface GroupByOption {
  [key: string]: {
    label: string
    sub: string
    key: string,
    value: string,
    groupFunc: any
  }
}

export const GROUP_BY_OPTIONS: GroupByOption = {
  RULE: {
    label: 'Group by Rule',
    sub: 'Rule',
    key: 'RULE',
    value: 'rule',
    groupFunc: groupByRule
  },
  SITE: {
    label: 'Group by Site',
    sub: 'Site',
    key: 'SITE',
    value: 'site',
    groupFunc: groupBySite
  }
}

interface SortByOption {
  [key: string]: {
    label: string
    sub: string
    key: string,
    value: string,
    sortFn: any
  }
}

export const SORT_BY_OPTIONS: SortByOption = {
  HITS: {
    label: 'Number of Hits',
    sub: 'Hits',
    key: 'HITS',
    value: 'Hits',
    sortFn: (groups: Array<Insite.Insights.ComposedInsight[]>) => {
      return (
        orderBy(groups, g => sumHits(g), 'desc')
      )
    }
  },
  EQUIPS: {
    label: 'Number of Equips',
    value: 'Equips',
    sub: 'Equips',
    key: 'EQUIPS',
    sortFn: (groups: Array<Insite.Insights.ComposedInsight[]>) => (
      orderBy(groups, g => getGroupEquipCount(g), 'desc')
    )
  },
  UNASSOCIATED_EQUIPS: {
    label: 'Number of Unassociated Equips',
    value: 'Unassociated Equips',
    sub: 'Equips',
    key: 'UNASSOCIATED_EQUIPS',
    sortFn: (groups: Array<Insite.Insights.ComposedInsight[]>) => (
      orderBy(groups, g => getGroupUnassociatedEquipCount(g), 'desc')
    )
  },
  INSIGHTS_DESC: {
    label: 'Insight (High-Low)',
    sub: 'Insights',
    value: 'Insights Desc',
    key: 'INSIGHTS_DESC',
    sortFn: (groups: Array<Insite.Insights.ComposedInsight[]>, groupByOption: string) => {
      if (groupByOption === GROUP_BY_OPTIONS.RULE.key) {
        return orderBy(groups, g => g.length, 'desc')
      }

      if (groupByOption === GROUP_BY_OPTIONS.SITE.key) {
        return orderBy(groups, g => g.length, 'desc')
      }
      return groups
    }
  },
  INSIGHTS_ASC: {
    label: 'Insight (Low-High)',
    sub: 'Insights',
    value: 'Insights ASC',
    key: 'INSIGHTS_ASC',
    sortFn: (groups: Array<Insite.Insights.ComposedInsight[]>, groupByOption: string) => {
      if (groupByOption === GROUP_BY_OPTIONS.RULE.key) {
        return orderBy(groups, g => g.length, 'asc')
      }

      if (groupByOption === GROUP_BY_OPTIONS.SITE.key) {
        return orderBy(groups, g => g.length, 'asc')
      }
      return groups
    }
  },
  A_Z: {
    label: 'Alphabetical (A-Z)',
    sub: 'A-Z',
    value: 'A-Z',
    key: 'A_Z',
    sortFn: (groups: Array<Insite.Insights.ComposedInsight[]>, groupByOption: string) => {
      if (groupByOption === GROUP_BY_OPTIONS.RULE.key) {
        return orderBy(groups, g => `${g[0].ruleName}`.toLowerCase(), 'asc')
      }

      if (groupByOption === GROUP_BY_OPTIONS.SITE.key) {
        return orderBy(groups, g => `${g[0].site.label}`.toLowerCase(), 'asc')
      }

      return groups
    }
  },
  Z_A: {
    label: 'Alphabetical (Z-A)',
    sub: 'Z-A',
    value: 'Z-A',
    key: 'Z_A',
    sortFn: (groups: Array<Insite.Insights.ComposedInsight[]>, groupByOption: string) => {
      if (groupByOption === GROUP_BY_OPTIONS.RULE.key) {
        return orderBy(groups, g => `${g[0].ruleName}`.toLowerCase(), 'desc')
      }

      if (groupByOption === GROUP_BY_OPTIONS.SITE.key) {
        return orderBy(groups, g => `${g[0].site.label}`.toLowerCase(), 'desc')
      }

      return groups
    }
  }
}
